/**
 * PATH: apps/qa/src/components/ReportView.tsx
 * PURPOSE: Professional QA inspection report - orchestrator that composes
 *          sub-components for the report document layout.
 */

import { useState, useEffect } from 'react'
import { SignOffPanel } from './inline-report/SignOffPanel'
import { EditableNote } from './EditableNote'
import { PhotoPlate } from './PhotoPlate'
import { AreaSection } from './AreaSection'
import { SectionSelector } from './SectionSelector'
import { useReportData } from './useReportData'
import type { ReportViewProps } from './reportHelpers'
import {
  FileText, Loader2, XCircle, PenTool,
  Plus, SlidersHorizontal, Pencil,
} from 'lucide-react'

export default function ReportView({ inspectionId, inspectionName, inspection, floors, onActionsReady }: ReportViewProps) {
  const [showSignOff, setShowSignOff] = useState(false)
  const [showSections, setShowSections] = useState(false)

  const rd = useReportData(inspectionId, inspectionName, floors)

  useEffect(() => {
    onActionsReady?.({
      onSave: () => rd.saveMutation.mutate(),
      onSignOff: () => { if (!rd.activeDraft) rd.createDraftMutation.mutate(); else setShowSignOff(true) },
      onExport: rd.handleExportPdf,
      isSaving: rd.saveMutation.isPending,
      isExporting: rd.isExporting,
    })
  }, [rd.activeDraft?.id, rd.saveMutation.isPending, rd.isExporting]) // eslint-disable-line react-hooks/exhaustive-deps

  const docNumber = `QA-${(inspection.job_id as string || '0000').toString().slice(-4)}-${String(rd.activeDraft?.version || 1).padStart(2, '0')}`
  const createdDate = new Date(inspection.created_at as string).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })

  let floorNum = 0

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 px-1">
        <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
        {rd.isEditingTitle ? (
          <input autoFocus value={rd.title} onChange={e => rd.setTitle(e.target.value)}
            onBlur={() => { rd.setIsEditingTitle(false); if (rd.activeDraft && rd.title !== rd.activeDraft.title) rd.autoSaveMutation.mutate({ title: rd.title }) }}
            onKeyDown={e => { if (e.key === 'Enter') { rd.setIsEditingTitle(false); if (rd.activeDraft && rd.title !== rd.activeDraft.title) rd.autoSaveMutation.mutate({ title: rd.title }) } }}
            className="text-sm font-bold text-slate-800 bg-transparent border-b-2 border-blue-500 focus:outline-none flex-1 min-w-[200px]" />
        ) : (
          <button onClick={() => rd.setIsEditingTitle(true)} className="text-sm font-bold text-slate-800 hover:text-blue-600 transition-colors truncate flex items-center gap-1.5 group">
            {rd.title || 'Untitled Report'}
            <Pencil className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        )}
        {rd.activeDraft && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-mono font-bold">v{rd.activeDraft.version}</span>}
        {rd.drafts.length > 1 && (
          <select value={rd.activeDraftId || ''} onChange={e => rd.setActiveDraftId(e.target.value)} className="text-xs rounded border border-slate-300 bg-white px-2 py-1 text-slate-700">
            {rd.drafts.map(d => <option key={d.id} value={d.id}>{d.title} (v{d.version})</option>)}
          </select>
        )}
        <button onClick={() => rd.createDraftMutation.mutate()} disabled={rd.createDraftMutation.isPending}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-slate-300 hover:bg-slate-100"><Plus className="w-3 h-3" /> New</button>
        <button onClick={() => setShowSections(true)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-slate-300 hover:bg-slate-100"><SlidersHorizontal className="w-3 h-3" /> Sections</button>
      </div>

      {/* Report Document */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {/* Cover */}
        <div className="border-b-4 border-blue-600 px-8 py-8">
          <div className="flex items-start justify-between mb-2">
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-[0.2em]">Quality Assurance Inspection Report</p>
            <span className="text-[10px] font-bold text-slate-500 font-mono bg-slate-100 px-2 py-0.5 rounded">{docNumber}</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 leading-tight">{inspectionName}</h1>
          <div className="mt-5 border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="px-3 py-2 bg-slate-50 text-slate-500 font-bold uppercase text-[10px] w-28">Job</td>
                  <td className="px-3 py-2 text-slate-800 font-semibold">{inspection.job_name as string || inspection.job_id as string || '\u2014'}</td>
                  <td className="px-3 py-2 bg-slate-50 text-slate-500 font-bold uppercase text-[10px] w-28 border-l border-slate-100">Date</td>
                  <td className="px-3 py-2 text-slate-800 font-semibold">{createdDate}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="px-3 py-2 bg-slate-50 text-slate-500 font-bold uppercase text-[10px]">Category</td>
                  <td className="px-3 py-2 text-slate-800 capitalize">{(inspection.category as string || '\u2014').replace(/_/g, ' ')}</td>
                  <td className="px-3 py-2 bg-slate-50 text-slate-500 font-bold uppercase text-[10px] border-l border-slate-100">Revision</td>
                  <td className="px-3 py-2 text-slate-800 font-mono font-semibold">{rd.activeDraft?.version || 1}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 bg-slate-50 text-slate-500 font-bold uppercase text-[10px]">Progress</td>
                  <td className="px-3 py-2 text-slate-800 font-semibold">{rd.lockedAreas}/{rd.totalAreas} areas complete</td>
                  <td className="px-3 py-2 bg-slate-50 text-slate-500 font-bold uppercase text-[10px] border-l border-slate-100">Evidence</td>
                  <td className="px-3 py-2 text-slate-800">{rd.totalPhotos} photo{rd.totalPhotos !== 1 ? 's' : ''}, {rd.totalNotes} note{rd.totalNotes !== 1 ? 's' : ''}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {rd.totalAreas > 0 && (
            <div className="mt-4">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${rd.completionPct}%` }} />
              </div>
              <p className="text-[10px] text-slate-500 mt-1">{rd.completionPct}% complete</p>
            </div>
          )}
          <EditableNote noteKey="cover_note" value={rd.userNotes.cover_note || ''} onChange={rd.handleNoteChange} placeholder="Add an executive summary or introduction..." />
          {rd.activeDraft?.sign_off_data?.signer_name && (
            <div className="mt-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="flex items-start gap-3">
                <PenTool className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-emerald-800">Signed by {rd.activeDraft.sign_off_data.signer_name}</p>
                  {(rd.activeDraft.sign_off_data.designation || rd.activeDraft.sign_off_data.company) && (
                    <p className="text-xs text-emerald-700 mt-0.5">{rd.activeDraft.sign_off_data.designation}{rd.activeDraft.sign_off_data.designation && rd.activeDraft.sign_off_data.company && ', '}{rd.activeDraft.sign_off_data.company}</p>
                  )}
                  <p className="text-[10px] text-emerald-600 mt-1">{new Date(rd.activeDraft.sign_off_data.signed_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
            </div>
          )}
          {rd.isExporting && <p className="mt-3 text-xs text-blue-600 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating PDF...</p>}
          {rd.exportError && <p className="mt-3 text-xs text-red-600 flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5" /> {rd.exportError}</p>}
        </div>

        {/* Table of Contents */}
        {rd.filteredFloors.length > 0 && (
          <div className="px-8 py-5 border-b border-slate-200 bg-slate-50/50">
            <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">Table of Contents</h2>
            <div className="space-y-0.5">
              {(() => {
                let tocNum = 0
                return rd.filteredFloors.map(floor => {
                  tocNum++
                  const areas = (floor.areas || []) as Record<string, unknown>[]
                  return (
                    <div key={floor.id as string}>
                      <div className="flex items-center gap-2 py-0.5">
                        <span className="text-xs font-bold text-blue-600 font-mono w-8">{tocNum}.0</span>
                        <span className="text-xs font-bold text-slate-800">{floor.floor_name as string}</span>
                        <span className="flex-1 border-b border-dotted border-slate-300 mx-2" />
                        <span className="text-[10px] text-slate-400">{areas.length} area{areas.length !== 1 ? 's' : ''}</span>
                      </div>
                      {areas.map((area, aIdx) => (
                        <div key={area.id as string} className="flex items-center gap-2 py-0.5 pl-4">
                          <span className="text-xs text-slate-500 font-mono w-8">{tocNum}.{aIdx + 1}</span>
                          <span className="text-xs text-slate-600">{area.area_name as string}</span>
                          <span className="text-[10px] text-slate-400 ml-1">({area.area_code as string})</span>
                        </div>
                      ))}
                    </div>
                  )
                })
              })()}
            </div>
          </div>
        )}

        {/* Floor sections */}
        {rd.filteredFloors.map(floor => {
          floorNum++
          const areas = (floor.areas || []) as Record<string, unknown>[]
          return (
            <div key={floor.id as string} className="border-b border-slate-200 last:border-b-0">
              <div className="bg-slate-50 px-8 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <span className="text-blue-600">{floorNum}.0</span>
                  {floor.floor_name as string}
                </h2>
              </div>
              <div className="px-8 pt-4">
                <EditableNote noteKey={`floor_${floor.id}_note`} value={rd.userNotes[`floor_${floor.id}_note`] || ''} onChange={rd.handleNoteChange} placeholder="Add a note for this floor section..." />
              </div>
              <div className="px-8 py-6">
                {areas.length > 0 ? areas.map((area, aIdx) => (
                  <AreaSection key={area.id as string} area={area} sectionNumber={`${floorNum}.${aIdx + 1}`}
                    evidence={rd.evidenceByArea[area.id as string] || []} noteValue={rd.userNotes[`area_${area.id}_note`] || ''} onNoteChange={rd.handleNoteChange} />
                )) : <p className="text-sm text-slate-500 italic">No areas included in this section.</p>}
              </div>
            </div>
          )
        })}

        {(rd.evidenceByArea['__general__'] || []).length > 0 && (
          <div className="border-b border-slate-200 px-8 py-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">General Evidence</h2>
            <PhotoPlate photos={(rd.evidenceByArea['__general__'] || []).filter(e => (e.type === 'photo' || e.type === 'video') && e.asset_url)} />
          </div>
        )}

        <div className="px-8 py-6 bg-slate-50">
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-2">Closing Remarks</h2>
          <EditableNote noteKey="closing_note" value={rd.userNotes.closing_note || ''} onChange={rd.handleNoteChange} placeholder="Add closing remarks, recommendations, or next steps..." />
        </div>

        {!rd.activeDraft?.sign_off_data?.signer_name && (
          <div className="px-8 py-6 border-t border-slate-200">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4">Sign-off</h2>
            <div className="grid grid-cols-2 gap-6 text-xs text-slate-500">
              <div>
                <p className="font-bold uppercase text-[10px] mb-6">Signature</p><div className="border-b border-slate-300 mb-3" />
                <p className="font-bold uppercase text-[10px] mb-4">Name</p><div className="border-b border-slate-300 mb-3" />
                <p className="font-bold uppercase text-[10px] mb-4">Title</p><div className="border-b border-slate-300" />
              </div>
              <div>
                <p className="font-bold uppercase text-[10px] mb-6">Date</p><div className="border-b border-slate-300 mb-3" />
                <p className="font-bold uppercase text-[10px] mb-4">Company</p><div className="border-b border-slate-300 mb-3" />
                <p className="font-bold uppercase text-[10px] mb-4">Contact</p><div className="border-b border-slate-300" />
              </div>
            </div>
          </div>
        )}

        {rd.filteredFloors.length === 0 && (
          <div className="py-16 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">No sections selected</p>
            <button onClick={() => setShowSections(true)} className="mt-2 text-sm text-blue-600 hover:underline">Select report sections</button>
          </div>
        )}

        <div className="px-8 py-3 bg-slate-100 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-500">
          <span>Generated by Quotech QA</span>
          <span className="font-mono font-bold">{docNumber}</span>
          <span>{new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
        </div>
      </div>

      {showSignOff && rd.activeDraft && (
        <SignOffPanel draftId={rd.activeDraft.id} inspectionId={inspectionId} onClose={() => { setShowSignOff(false); rd.refetchDrafts() }} />
      )}
      {showSections && (
        <SectionSelector floors={floors} includedSections={rd.includedSections} onToggleFloor={rd.handleToggleFloor} onToggleArea={rd.handleToggleArea} onClose={() => setShowSections(false)} />
      )}
    </div>
  )
}
